import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useQuery, useMutation } from '@apollo/client/react';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import type { PlaylistItem, BroadcastLivePlayItem } from '@jwp/ott-common/types/playlist';
import { GET_MY_VIDEOS, GetMyVideosData, GetMyVideosVariables, MyVideo } from '@jwp/ott-common/src/graphql/query/reels.queries';
import { GetAllBroadcastLiveVariables, GetAllBroadcastLiveData, GET_ALL_BROADCASTS_LIVE } from '@jwp/ott-common/src/graphql/query/broadcastLive.queries';
import { DELETE_MEDIA_BY_MEDIA_ID, DeleteMediaByMediaIdData } from '@jwp/ott-common/src/graphql/mutation/reels.mutation';
import BackwardArrow from '@jwp/ott-theme/assets/icons/arrow_backward.svg';
import PlusIcon from '@jwp/ott-theme/assets/icons/plus.svg?react';
import CheckIcon from '@jwp/ott-theme/assets/icons/check.svg?react';
import InfoIcon from '@jwp/ott-theme/assets/icons/info.svg?react';
import EditIcon from '@jwp/ott-theme/assets/icons/edit.svg?react';
import DeleteIcon from '@jwp/ott-theme/assets/icons/delete.svg?react';
import NoResultsIcon from '@jwp/ott-theme/assets/icons/no_result.svg?react';
import env from '@jwp/ott-common/src/env';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { PATH_VIDEO_PREVIEW, PATH_HOME, PATH_MY_VIDEO_DETAILS, PATH_BROADCAST_LIVE_DETAILS, PATH_BROADCAST_LIVE_PLAY } from '@jwp/ott-common/src/paths';
import { isBroadcastLiveEnded, getBroadcastCardTag } from '@jwp/ott-common/src/utils/broadcastLive';
import { MediaStatus } from '@jwp/ott-common/src/utils/liveEvent';
import { PERMISSIONS, hasPermission } from '@jwp/ott-common/src/constants';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';

import { useAccountStore } from '../../../../common/src/stores/AccountStore';
import { useTheme } from '../../../../../ThemeContext';
import IconButton from '../IconButton/IconButton';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import Popover from '../Popover/Popover';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import Dialog from '../Dialog/Dialog';
import Card from '../Card/Card';
import Alert from '../Alert/Alert';
import styles from './MyVideos.module.scss';


const MyVideos = () => {
  const { t } = useTranslation(['user', 'video', 'common']);
  const { universalStyles } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;
  const { isUploadEnabled, isReelUploadEnabled, isVideoUploadEnabled, isBroadcastUploadEnabled } = useAppConfig();
  const isReelsTabEnabled = isUploadEnabled && isReelUploadEnabled;
  const isVideosTabEnabled = isUploadEnabled && isVideoUploadEnabled;
  const isBroadcastsTabEnabled = isUploadEnabled && isBroadcastUploadEnabled && hasPermission(user, PERMISSIONS.VIEW_BROADCAST_LIVE);
  const hasAnyUploadEnabled = isReelsTabEnabled || isVideosTabEnabled || isBroadcastsTabEnabled;

  const client = useMemo(() => getAuthApolloClient(), []);

  const getDefaultTab = React.useCallback((): 'reels' | 'videos' | 'broadcasts' => {
    const tab = queryParams.get('tab');

    if (tab === 'videos' && isVideosTabEnabled) { return 'videos'; }
    if (tab === 'reels' && isReelsTabEnabled) { return 'reels'; }
    if (tab === 'broadcasts' && isBroadcastsTabEnabled) { return 'broadcasts'; }
    if (isReelsTabEnabled) { return 'reels'; }
    if (isVideosTabEnabled) { return 'videos'; }
    if (isBroadcastsTabEnabled) { return 'broadcasts'; }

    return 'reels';
  }, [queryParams, isReelsTabEnabled, isVideosTabEnabled, isBroadcastsTabEnabled]);

  const [activeTab, setActiveTab] = useState<'reels' | 'videos' | 'broadcasts'>(getDefaultTab());
  const setPreviewUrl = useUploadStore((state) => state.setPreviewUrl);
  const setValues = useUploadStore((state) => state.setValues);
  const setReturnPath = useUploadStore((state) => state.setReturnPath);
  const setMediaId = useUploadStore((state) => state.setMediaId);
  const setDuration = useUploadStore((state) => state.setDuration);
  const setThumbnail = useUploadStore((state) => state.setThumbnail);
  const setInitiated = useUploadStore((state) => state.setInitiated);
  const resetUploadStore = useUploadStore((state) => state.reset);

  useEffect(() => {
    setActiveTab(getDefaultTab());
  }, [getDefaultTab]);

  const u = queryParams.get('u');
  const [lastU, setLastU] = useState<string | null>(u);

  const [menuAnchor, setMenuAnchor] = useState<{ x: number; y: number; isBottom?: boolean } | null>(null);
  const [selectedItem, setSelectedItem] = useState<MyVideo | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const getQueryVariables = (type: string): GetMyVideosVariables => ({
    filter: {
      maType: { equalTo: type },
      maUploadStatus: { equalTo: 'UPLOADED' },
      maDeletedAt: { isNull: true },
      maUploadedBy: { equalTo: user?.id || '' },
    },
    first: 12,
    offset: 0,
  });

  const reelsQuery = useQuery<GetMyVideosData, GetMyVideosVariables>(GET_MY_VIDEOS, {
    variables: getQueryVariables('REELS'),
    skip: !user?.id,
    notifyOnNetworkStatusChange: true,
  });

  const videosQuery = useQuery<GetMyVideosData, GetMyVideosVariables>(GET_MY_VIDEOS, {
    variables: getQueryVariables('VIDEOS'),
    skip: !user?.id,
    notifyOnNetworkStatusChange: true,
  });

  const getBroadcastqueryVariables = (): GetAllBroadcastLiveVariables => ({
    limit: 20,
    offset: 0,
  })

  const broadcastQuery = useQuery<GetAllBroadcastLiveData, GetAllBroadcastLiveVariables>(GET_ALL_BROADCASTS_LIVE, {
    client,
    variables: getBroadcastqueryVariables(),
    notifyOnNetworkStatusChange: true,
  })

  const [deleteMediaApproval, { loading: deleteLoading }] = useMutation<DeleteMediaByMediaIdData, { maId: string }>(DELETE_MEDIA_BY_MEDIA_ID, {
    onCompleted: () => {
      setConfirmationOpen(false);
      setSuccessOpen(true);
      reelsQuery.refetch();
      videosQuery.refetch();
    },
    onError: (error) => {
      console.error('Delete failed:', error);
      setErrorMessage(t('video:delete_error', { defaultValue: 'Failed to delete video. Please try again.' }));
      setErrorOpen(true);
    },
  });

  useEffect(() => {
    if ((lastU === 'upload-success' || lastU === 'edit-success') && !u) {
      reelsQuery.refetch();
      videosQuery.refetch();
    }
    setLastU(u);
  }, [u, lastU, reelsQuery, videosQuery]);

  useEffect(() => {
    const handleResize = () => setMenuAnchor(null);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true); // Close on scroll too as it's fixed position
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, []);

  const handleBackClick = () => {
    navigate(PATH_HOME, { replace: true });
  };

  const broadcastsTotalList = broadcastQuery.data?.getAllBroadcastLive?.BroadcastLiveItems || [];
  const broadcastsTotalCount = broadcastQuery.data?.getAllBroadcastLive?.total || 0;
  const currentQuery = activeTab === 'reels' ? reelsQuery : videosQuery;
  const items = currentQuery.data?.allMediaApprovals?.nodes || [];
  const totalCount = activeTab === 'broadcasts' ? broadcastsTotalCount : (currentQuery.data?.allMediaApprovals?.totalCount ?? 0);
  const loading = activeTab === 'broadcasts' ? broadcastQuery.loading : currentQuery.loading;

  // const broadcasts = broadcastQuery.data?.getAllBroadcastLive.BroadcastLiveItems ?? [];
  // const total = broadcastQuery.data?.getAllBroadcastLive.total ?? 0;

  const handleLoadMore = () => {
    if (!currentQuery || items.length >= totalCount) { return; }
    currentQuery.fetchMore({
      variables: { offset: items.length },
      updateQuery: (prevResult: GetMyVideosData, { fetchMoreResult }: { fetchMoreResult?: GetMyVideosData }) => {
        if (!fetchMoreResult) { return prevResult; }
        return {
          allMediaApprovals: {
            ...fetchMoreResult.allMediaApprovals,
            nodes: [...(prevResult.allMediaApprovals?.nodes || []), ...(fetchMoreResult.allMediaApprovals?.nodes || [])],
          },
        };
      },
    });
  };

  const handleLoadBroadcastsMore = () => {
    if (broadcastsTotalList.length >= totalCount) { return; }

    broadcastQuery.fetchMore({
      variables: {
        ...getBroadcastqueryVariables(),
        offset: broadcastsTotalList.length,
      },
      updateQuery: (
        prevResult: GetAllBroadcastLiveData,
        {
          fetchMoreResult,
        }: {
          fetchMoreResult?: GetAllBroadcastLiveData;
        },
      ) => {
        if (!fetchMoreResult) { return prevResult; }

        return {
          getAllBroadcastLive: {
            ...fetchMoreResult.getAllBroadcastLive,
            BroadcastLiveItems: [
              ...(prevResult.getAllBroadcastLive?.BroadcastLiveItems || []),
              ...(fetchMoreResult.getAllBroadcastLive?.BroadcastLiveItems || []),
            ],
          },
        };
      },
    });
  };

  const handleShowDetails = () => {
    setMenuAnchor(null);
    if (selectedItem) {
      setInitiated(true);
      navigate(createURLFromLocation(location, { u: 'reel-details', id: selectedItem.maId }));
    }
  };

  const handleEdit = () => {
    setMenuAnchor(null);
    if (selectedItem) {
      setMediaId(selectedItem.maId);
      setPreviewUrl(`${env.APP_BASE_URL || ''}${selectedItem.maS3MediaKeyUrl}`);
      setDuration(selectedItem.maDuration ? Number(selectedItem.maDuration) : null);

      let tagsString = '';
      if (Array.isArray(selectedItem.maTags)) {
        tagsString = selectedItem.maTags.join(', ');
      } else if (typeof selectedItem.maTags === 'string') {
        tagsString = selectedItem.maTags;
      }

      if (selectedItem.maType === 'REELS') {
        setThumbnail(null, `${env.APP_BASE_URL || ''}${selectedItem.maS3ThumbnailKeyUrl}`);
        setValues({
          title: selectedItem.maTitle,
          description: selectedItem.maDescription,
          category: selectedItem.maCategory,
          tags: tagsString,
        });
      } else {
        const useUploadStoreState = useUploadStore.getState();
        useUploadStoreState.setStaticThumbnail(null, `${env.APP_BASE_URL || ''}${selectedItem.maS3ThumbnailKeyUrl}`);

        // Find motion thumbnail from additional images or other properties if present
        const motionThumb = selectedItem.maAdditionalImages?.find((img) => img.maAdditionalImagesLabelValue === 'video');
        if (motionThumb) {
          useUploadStoreState.setMotionThumbnail(null, `${env.APP_BASE_URL || ''}${motionThumb.maAdditionalImagesS3KeyUrl}`);
        } else {
          useUploadStoreState.setMotionThumbnail(null, null);
        }

        const additionalImgs =
          selectedItem.maAdditionalImages?.filter(
            (img) =>
              !img.maIsThumbnail &&
              img.maAdditionalImagesLabelValue !== 'video' &&
              img.maAdditionalImagesLabelValue !== 'static' &&
              img.maAdditionalImagesLabelName !== 'Static Thumbnail',
          ) || [];
        const finalImgs = additionalImgs.map((img) => ({
          file: new File([], img.maAdditionalImagesLabelName),
          thumbnailUrl: `${env.APP_BASE_URL || ''}${img.maAdditionalImagesS3KeyUrl}`,
          labelId: img.maAdditionalImagesLabelId,
          labelName: img.maAdditionalImagesLabelName,
          labelValue: img.maAdditionalImagesLabelValue,
        }));
        useUploadStoreState.setAdditionalImages(finalImgs);
        useUploadStoreState.setOriginalAdditionalImages(finalImgs);

        const genreIds = selectedItem.genreDropdownsByMaId?.nodes ? selectedItem.genreDropdownsByMaId.nodes.map((n: any) => n.dropdownByDdId.ddId) : [];
        const ageRatingId = selectedItem.dropdownByAgeRatingId?.ddId || '';

        useUploadStoreState.setVideoValues({
          title: selectedItem.maTitle,
          description: selectedItem.maDescription || '',
          genreIds,
          ageRatingId,
          tags: tagsString,
        });
      }

      setReturnPath(location.pathname + location.search);
      setInitiated(true);
      navigate(createURLFromLocation(location, { u: selectedItem.maType === 'REELS' ? 'upload-reel' : 'upload-video' }));
    }
  };

  const handleDelete = () => {
    setMenuAnchor(null);
    setConfirmationOpen(true);
  };

  const reelsCount = reelsQuery.data?.allMediaApprovals?.totalCount || 0;
  const videosCount = videosQuery.data?.allMediaApprovals?.totalCount || 0;

  const handleCreateLiveStream = () => {
    navigate(createURLFromLocation(location, { u: 'create-live-stream' }));
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerTitlePart}>
          <IconButton className={styles.iconButton} onClick={handleBackClick}>
            <img src={BackwardArrow} alt="Back" />
          </IconButton>
          <h1>{t('nav.videos')}</h1>
        </div>
      </header>

      <div className={styles.actionsBar}>
        <div className={styles.tabs}>
          {isReelsTabEnabled && (
            <Button
              label={t('video:reels_count', { count: reelsCount })}
              className={classNames(styles.tab, { [styles.activeTab]: activeTab === 'reels' })}
              onClick={() => {
                setActiveTab('reels');
                navigate(createURLFromLocation(location, { tab: null }));
              }}
              variant="text"
              active={activeTab === 'reels'}
            />
          )}
          {isVideosTabEnabled && (
            <Button
              label={t('video:videos_count', { count: videosCount })}
              className={classNames(styles.tab, { [styles.activeTab]: activeTab === 'videos' })}
              onClick={() => {
                setActiveTab('videos');
                navigate(createURLFromLocation(location, { tab: 'videos' }));
              }}
              variant="text"
              active={activeTab === 'videos'}
            />
          )}
          {isBroadcastsTabEnabled && (
            <Button
              label={t('video:broadcast_live_count', { count: broadcastsTotalCount })}
              className={classNames(styles.tab, { [styles.activeTab]: activeTab === 'broadcasts' })}
              onClick={() => {
                setActiveTab('broadcasts');
                navigate(createURLFromLocation(location, { tab: 'broadcasts' }));
              }}
              variant="text"
              active={activeTab === 'broadcasts'}
            />
          )}
        </div>

        {isLoggedIn &&
          hasPermission(user, PERMISSIONS.CREATE_MEDIA) &&
          hasAnyUploadEnabled &&
          ((activeTab === 'reels' && isReelsTabEnabled) || (activeTab === 'videos' && isVideosTabEnabled)) && (
            <Button
              label={activeTab === 'reels' ? t('video:upload_reel') : t('upload_video.title', { defaultValue: 'Upload Video' })}
              startIcon={<Icon icon={PlusIcon} />}
              onClick={() => {
                resetUploadStore();
                setInitiated(true);
                navigate(createURLFromLocation(location, { u: activeTab === 'reels' ? 'upload-reel' : 'upload-video' }));
              }}
              className={styles.uploadButton}
              variant="contained"
              color="primary"
            />
          )}

        {activeTab === 'broadcasts' && isLoggedIn && hasPermission(user, PERMISSIONS.CREATE_BROADCAST_LIVE) && (
          <Button
            label={t('video:broadcast_live.create_button')}
            startIcon={<Icon icon={PlusIcon} />}
            onClick={handleCreateLiveStream}
            className={styles.uploadButton}
            variant="contained"
            color="primary"
          />
        )}
      </div>

      {activeTab === 'broadcasts' && broadcastsTotalList.length === 0 && !loading ? (
        <div className={styles.broadcastEmptyState}>
          <NoResultsIcon className={styles.broadcastEmptyIcon} aria-hidden="true" />
          <p className={styles.broadcastEmptyText}>{t('video:broadcast_live.empty')}</p>
        </div>
      ) : activeTab !== 'broadcasts' && items.length === 0 && !loading ? (
        <p className={styles.emptyVideos} style={universalStyles.primaryTextColor}>
          {t('video:no_videos')}
        </p>
      ) : activeTab === 'broadcasts' ? <div className={classNames(styles.grid, { [styles.videosGrid]: true })}>
        {broadcastsTotalList.map((item) => {
          const cardTag = getBroadcastCardTag(item);
          const cardItem: BroadcastLivePlayItem = {
            mediaid: item.blId,
            title: item.blTitle,
            duration: 0,
            cardImage: item.mediaApproval?.maS3ThumbnailKeyUrl
              ? `${env.APP_BASE_URL || ''}${item.mediaApproval.maS3ThumbnailKeyUrl}`
              : '',
            images: [],
            link: '',
            pubdate: 0,
            sources: [],
            feedid: '',
            description: item.blDescription,
            mediaStatus: cardTag?.type === 'live' ? MediaStatus.LIVE : cardTag?.type === 'scheduled' ? MediaStatus.SCHEDULED : undefined,
            scheduledStart: cardTag?.scheduledStart,
          } as unknown as BroadcastLivePlayItem;

          const canEditOrDelete = hasPermission(user, PERMISSIONS.UPDATE_MEDIA) || hasPermission(user, PERMISSIONS.DELETE_MEDIA);
          const showMenu = canEditOrDelete;
          return (
            <Card
              key={item.blId}
              item={cardItem}
              url={'#'}
              posterAspect={'16:9'}
              status={item.blStatus}
              isOverlayTestVisible={true}
              isBordered={true}
              isReels={false}
              onMenuClick={
                showMenu
                  ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // if (menuAnchor && selectedItem?.maId === item.maId) {
                    //   setMenuAnchor(null);
                    // } else {
                    //   setSelectedItem(item);
                    //   const rect = e.currentTarget.getBoundingClientRect();
                    //   const popoverHeight = 250;
                    //   const isBottom = rect.bottom + popoverHeight > window.innerHeight;
                    //   setMenuAnchor({ x: rect.right, y: isBottom ? rect.top : rect.bottom, isBottom });
                    // }
                  }
                  : undefined
              }
              onClick={(e) => {
                e.preventDefault();
                const returnPath = encodeURIComponent(location.pathname + location.search);
                const isEnded = isBroadcastLiveEnded(item.blEndTime);
                if (isEnded && item.blHlsPlayoutUrl) {
                  navigate(`${PATH_BROADCAST_LIVE_PLAY}/${item.blId}?returnPath=${returnPath}`);
                } else {
                  navigate(`${PATH_BROADCAST_LIVE_DETAILS}/${item.blId}?returnPath=${returnPath}`);
                }
              }}
            />
          );
        })}
      </div> :
        <div className={classNames(styles.grid, { [styles.reelsGrid]: activeTab === 'reels', [styles.videosGrid]: activeTab === 'videos' })}>
          {items.map((item) => {
            const cardItem: PlaylistItem = {
              mediaid: item.maId,
              title: item.maTitle,
              duration: activeTab === 'reels' && item.maDuration ? Number(item.maDuration) / 1000 : 0,
              cardImage: `${env.APP_BASE_URL || ''}${item.maS3ThumbnailKeyUrl}`,
              images: [],
              link: '',
              pubdate: 0,
              sources: [],
              feedid: '',
              description: item.maDescription,
            } as unknown as PlaylistItem;

            const canEditOrDelete = hasPermission(user, PERMISSIONS.UPDATE_MEDIA) || hasPermission(user, PERMISSIONS.DELETE_MEDIA);
            const showMenu = activeTab === 'reels' || canEditOrDelete;
            return (
              <Card
                key={item.maId}
                item={cardItem}
                url={activeTab === 'reels' ? `${PATH_VIDEO_PREVIEW}/${item.maId}?returnPath=${encodeURIComponent(location.pathname)}` : '#'}
                posterAspect={activeTab === 'reels' ? '9:16' : '16:9'}
                status={item.maStatus}
                isReels={activeTab === 'reels'}
                isOverlayTestVisible={true}
                onMenuClick={
                  showMenu
                    ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (menuAnchor && selectedItem?.maId === item.maId) {
                        setMenuAnchor(null);
                      } else {
                        setSelectedItem(item);
                        const rect = e.currentTarget.getBoundingClientRect();
                        const popoverHeight = 250;
                        const isBottom = rect.bottom + popoverHeight > window.innerHeight;
                        setMenuAnchor({ x: rect.right, y: isBottom ? rect.top : rect.bottom, isBottom });
                      }
                    }
                    : undefined
                }
                onClick={(e) => {
                  e.preventDefault();
                  if (activeTab === 'reels') {
                    setPreviewUrl(`${env.APP_BASE_URL || ''}${item.maS3MediaKeyUrl}`);
                    setValues({
                      title: item.maTitle,
                      description: item.maDescription,
                      category: item.maCategory,
                      tags: Array.isArray(item.maTags) ? item.maTags.join(', ') : typeof item.maTags === 'string' ? item.maTags : '',
                    });
                    setReturnPath(location.pathname + location.search);
                    navigate(`${PATH_VIDEO_PREVIEW}/${item.maId}?returnPath=${encodeURIComponent(location.pathname + location.search)}`);
                  } else {
                    navigate(`${PATH_MY_VIDEO_DETAILS}/${item.maId}?returnPath=${encodeURIComponent(location.pathname + location.search)}`);
                  }
                }}
              />
            );
          })}
        </div>
      }

      {activeTab !== 'broadcasts' && items.length < totalCount && (
        <div className={styles.loadMoreContainer} style={{ textAlign: 'center', marginTop: '32px' }}>
          <Button label={loading ? t('common:loading') : t('common:load_more')} onClick={handleLoadMore} disabled={loading} variant="outlined" />
        </div>
      )}

      {activeTab == 'broadcasts' && broadcastsTotalList.length < totalCount && (
        <div className={styles.loadMoreContainer} style={{ textAlign: 'center', marginTop: '32px' }}>
          <Button label={loading ? t('common:loading') : t('common:load_more')} onClick={handleLoadBroadcastsMore} disabled={loading} variant="outlined" />
        </div>
      )}

      {/* Popover Menu */}
      <Popover
        isOpen={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        className={classNames(styles.popoverNoGap, menuAnchor?.isBottom && styles.popoverUp)}
        style={{
          position: 'fixed',
          top: menuAnchor?.y || 0,
          left: menuAnchor?.x || 0,
          transform: `translateX(-100%) ${menuAnchor?.isBottom ? 'translateY(-100%)' : ''}`,
          zIndex: 1000,
        }}
      >
        <div className={styles.popoverMenu}>
          {activeTab === 'reels' && (
            <>
              <button className={styles.popoverItem} onClick={handleShowDetails}>
                <Icon icon={InfoIcon} className={styles.popoverIcon} />
                <span>{t('video:show_details')}</span>
              </button>
              {hasPermission(user, PERMISSIONS.UPDATE_MEDIA) && <div className={styles.popoverDivider} />}
            </>
          )}
          {hasPermission(user, PERMISSIONS.UPDATE_MEDIA) && (
            <button className={styles.popoverItem} onClick={handleEdit}>
              <Icon icon={EditIcon} className={styles.popoverIcon} />
              <span>{activeTab === 'reels' ? t('video:edit_reel') : t('video:edit_video')}</span>
            </button>
          )}
          {hasPermission(user, PERMISSIONS.DELETE_MEDIA) && <div className={styles.popoverDivider} />}
          {hasPermission(user, PERMISSIONS.DELETE_MEDIA) && (
            <button className={classNames(styles.popoverItem, styles.deleteItem)} onClick={handleDelete}>
              <Icon icon={DeleteIcon} className={styles.popoverIcon} />
              <span>{activeTab === 'reels' ? t('video:delete_reel') : t('video:delete_video')}</span>
            </button>
          )}
        </div>
      </Popover>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmationOpen}
        variant="delete"
        title={activeTab === 'reels' ? t('video:delete_reel_title') : t('video:delete_video_title')}
        body={activeTab === 'reels' ? t('video:delete_reel_body') : t('video:delete_video_body')}
        confirmLabel={activeTab === 'reels' ? t('video:delete_reel_confirm') : t('video:delete_video_confirm')}
        closeLabel={t('common:no')}
        onConfirm={() => {
          if (selectedItem) {
            deleteMediaApproval({ variables: { maId: selectedItem.maId } });
          }
        }}
        onClose={() => setConfirmationOpen(false)}
        busy={deleteLoading}
      />

      {/* Success Dialog */}
      <Dialog open={successOpen} onClose={() => setSuccessOpen(false)} role="alertdialog">
        <div className={styles.successDialog}>
          <div className={styles.successIconContainer}>
            <Icon icon={CheckIcon} />
          </div>
          <h2>{activeTab === 'reels' ? t('upload:delete-success.title') : t('upload:delete-success-video.title')}</h2>
          <p>{activeTab === 'reels' ? t('upload:delete-success.description') : t('upload:delete-success-video.description')}</p>
          <Button label={t('common:okay', { defaultValue: 'Okay' })} variant="contained" color="primary" onClick={() => setSuccessOpen(false)} fullWidth />
        </div>
      </Dialog>

      <Alert open={errorOpen} message={errorMessage} onClose={() => setErrorOpen(false)} />
    </div>
  );
};

export default MyVideos;
